/**
 * Material-UI Theme Configuration
 */

import {
  createTheme,
  responsiveFontSizes,
  ThemeOptions,
  Shadows,
  alpha,
} from "@mui/material/styles";

// A soft, layered elevation scale (cool near-black ambient + key shadow) so
// every raised surface — cards, menus, dialogs, drawers — shares one calm
// depth language instead of MUI's default hard grey shadows. Indices map to
// the same slots MUI uses (1-2 cards, 4-8 menus/popovers, 16-24 modals).
const E = {
  0: "none",
  1: "0 1px 2px rgba(16,24,40,0.05), 0 1px 3px rgba(16,24,40,0.08)",
  2: "0 1px 2px rgba(16,24,40,0.06), 0 2px 6px rgba(16,24,40,0.08)",
  3: "0 2px 4px rgba(16,24,40,0.06), 0 4px 10px rgba(16,24,40,0.08)",
  4: "0 4px 8px rgba(16,24,40,0.06), 0 8px 16px rgba(16,24,40,0.10)",
  6: "0 6px 12px rgba(16,24,40,0.08), 0 12px 24px rgba(16,24,40,0.12)",
  8: "0 8px 18px rgba(16,24,40,0.10), 0 16px 32px rgba(16,24,40,0.14)",
  12: "0 12px 24px rgba(16,24,40,0.12), 0 24px 48px rgba(16,24,40,0.16)",
  16: "0 16px 32px rgba(16,24,40,0.14), 0 32px 64px rgba(16,24,40,0.18)",
  24: "0 24px 48px rgba(16,24,40,0.16), 0 40px 80px rgba(16,24,40,0.20)",
};
const softShadows = [
  E[0], E[1], E[2], E[3], E[4], E[4], E[6], E[6], E[8], E[8],
  E[8], E[12], E[12], E[12], E[12], E[16], E[16], E[16], E[16], E[16],
  E[24], E[24], E[24], E[24], E[24],
] as unknown as Shadows;

// Custom palette token layers so charts and the hifdh "stream" UI read colors
// from the theme (per-mode) instead of hardcoding hex that break dark mode.
declare module "@mui/material/styles" {
  interface Palette {
    chart: {
      /** Ordered categorical series colors for charts. */
      categorical: string[];
      /** Payment-method colors (collections chart). */
      cash: string;
      check: string;
      card: string;
      ach: string;
    };
    /** Hifdh review streams (report card / progress). */
    stream: {
      sabaq: string;
      sabqi: string;
      manzil: string;
    };
    /** Hifdh certificates: the gold of the title and frame (AA on the paper of each mode). */
    certificate: {
      gold: string;
    };
  }
  interface PaletteOptions {
    chart?: Palette["chart"];
    stream?: Palette["stream"];
    certificate?: Palette["certificate"];
  }
}

const lightChart = {
  categorical: ["#1976d2", "#2e7d32", "#9c27b0", "#ed6c02", "#0288d1", "#5c6bc0"],
  cash: "#2e7d32",
  check: "#9c27b0",
  card: "#1976d2",
  ach: "#ed6c02",
};
const darkChart = {
  categorical: ["#90caf9", "#66bb6a", "#ce93d8", "#ffa726", "#29b6f6", "#7986cb"],
  cash: "#66bb6a",
  check: "#ce93d8",
  card: "#90caf9",
  ach: "#ffa726",
};
const lightStream = { sabaq: "#1976d2", sabqi: "#ed6c02", manzil: "#7b1fa2" };
const darkStream = { sabaq: "#90caf9", sabqi: "#ffa726", manzil: "#ce93d8" };
// Certificate gold (WCAG contrast): #86601a is 5.7:1 on white; #e3b65c is 8.8:1 on the dark paper (#1e1e1e).
const lightCertificate = { gold: "#86601a" };
const darkCertificate = { gold: "#e3b65c" };

const lightThemeOptions: ThemeOptions = {
  palette: {
    mode: "light",
    primary: {
      // Darkened from #1976d2 so primary text/links and white-on-primary
      // avatars/buttons meet WCAG-AA (4.5:1) — the old blue sat at ~4.36.
      main: "#1460b8",
      light: "#e3f2fd",
      dark: "#0d47a1",
      contrastText: "#ffffff",
    },
    secondary: {
      main: "#9c27b0",
      light: "#f3e5f5",
      dark: "#7b1fa2",
      contrastText: "#ffffff",
    },
    success: {
      main: "#2e7d32",
      light: "#e8f5e9",
      dark: "#1b5e20",
    },
    error: {
      main: "#d32f2f",
      light: "#ffebee",
      dark: "#c62828",
    },
    warning: {
      main: "#ed6c02",
      light: "#fff3e0",
      // Darkened so warning-colored TEXT on a light surface (incl. the faint
      // #fafafa table row) meets WCAG-AA — the old #e65100 was ~4.2:1, orange
      // is unforgiving.
      dark: "#a15200",
    },
    info: {
      main: "#0288d1",
      light: "#e1f5fe",
      dark: "#01579b",
    },
    background: {
      default: "#f8f9fa",
      paper: "#ffffff",
    },
    text: {
      // #5f6368 reaches WCAG AA (>=4.5:1) on both background.paper (#fff)
      // and background.default (#f8f9fa); the prior #757575 was ~4.4:1 on the
      // default background — just under AA for the many small captions.
      primary: "#212121",
      secondary: "#5f6368",
    },
    divider: "#e6e8eb",
    chart: lightChart,
    stream: lightStream,
    certificate: lightCertificate,
  },
  shadows: softShadows,
  typography: {
    fontFamily: [
      "-apple-system",
      "BlinkMacSystemFont",
      '"Segoe UI"',
      "Roboto",
      '"Helvetica Neue"',
      "Arial",
      "sans-serif",
      '"Apple Color Emoji"',
      '"Segoe UI Emoji"',
      '"Segoe UI Symbol"',
    ].join(","),
    // Headings get slightly negative tracking + tighter leading for a more
    // refined, "designed" read; body copy gets generous line-height for
    // comfortable scanning. Sizes are unchanged so no layout reflow.
    h1: {
      fontWeight: 700,
      fontSize: "2.5rem",
      lineHeight: 1.15,
      letterSpacing: "-0.02em",
    },
    h2: {
      fontWeight: 700,
      fontSize: "2rem",
      lineHeight: 1.2,
      letterSpacing: "-0.018em",
    },
    h3: {
      fontWeight: 700,
      fontSize: "1.75rem",
      lineHeight: 1.22,
      letterSpacing: "-0.015em",
    },
    h4: {
      fontWeight: 700,
      fontSize: "1.5rem",
      lineHeight: 1.25,
      letterSpacing: "-0.012em",
    },
    h5: {
      fontWeight: 600,
      fontSize: "1.25rem",
      lineHeight: 1.3,
      letterSpacing: "-0.01em",
    },
    h6: {
      fontWeight: 600,
      fontSize: "1rem",
      lineHeight: 1.4,
      letterSpacing: "-0.006em",
    },
    body1: { lineHeight: 1.55 },
    body2: { lineHeight: 1.5 },
    button: {
      textTransform: "none",
      fontWeight: 600,
      letterSpacing: 0,
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: "8px 16px",
        },
        sizeLarge: {
          padding: "12px 24px",
          fontSize: "1rem",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        // A hairline border + soft ambient shadow gives every card a defined,
        // premium surface (matches the dashboard StatCard language) instead of
        // a shadow floating with no edge. Callback form so the border tracks
        // the per-mode divider token.
        root: ({ theme }) => ({
          borderRadius: 12,
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: theme.shadows[1],
          backgroundImage: "none",
        }),
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: "outlined",
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        // Premium field affordance: a subtle border that darkens on hover, and
        // a soft focus RING (instead of MUI's chunky 2px border jump) so focus
        // reads clearly without shifting layout. Error state gets a red ring.
        root: ({ theme }) => ({
          borderRadius: 8,
          transition: theme.transitions.create(["box-shadow", "border-color"], {
            duration: 150,
          }),
          "&:hover:not(.Mui-disabled):not(.Mui-error) .MuiOutlinedInput-notchedOutline":
            {
              borderColor: theme.palette.text.disabled,
            },
          "&.Mui-focused": {
            boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.16)}`,
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderWidth: 1,
          },
          "&.Mui-error.Mui-focused": {
            boxShadow: `0 0 0 3px ${alpha(theme.palette.error.main, 0.16)}`,
          },
        }),
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: "none",
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        // Callback form so the head surface + border come from the theme and
        // adapt to dark mode automatically (no per-mode duplicate override).
        root: ({ theme }) => ({
          "& .MuiTableCell-head": {
            backgroundColor: theme.palette.background.default,
            fontWeight: 600,
          },
        }),
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderBottom: `1px solid ${theme.palette.divider}`,
        }),
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          // Ensure minimum touch target size
          minWidth: 44,
          minHeight: 44,
        },
        sizeSmall: {
          minWidth: 44,
          minHeight: 44,
        },
      },
    },
  },
};

const darkThemeOptions: ThemeOptions = {
  palette: {
    mode: "dark",
    primary: {
      main: "#90caf9",
      light: "#1e3a5f",
      dark: "#42a5f5",
      contrastText: "#000000",
    },
    secondary: {
      main: "#ce93d8",
      light: "#4a1a4f",
      dark: "#ab47bc",
      contrastText: "#000000",
    },
    success: {
      main: "#66bb6a",
      light: "#1b3d1c",
      dark: "#4caf50",
    },
    error: {
      main: "#f44336",
      light: "#4a1a1a",
      dark: "#e57373",
    },
    warning: {
      main: "#ffa726",
      light: "#4a3a1a",
      dark: "#fb8c00",
    },
    info: {
      main: "#29b6f6",
      light: "#1a3d4a",
      dark: "#0288d1",
    },
    background: {
      default: "#121212",
      paper: "#1e1e1e",
    },
    text: {
      primary: "#ffffff",
      secondary: "#b0b0b0",
    },
    divider: "#2a2f36",
    chart: darkChart,
    stream: darkStream,
    certificate: darkCertificate,
  },
  shadows: softShadows,
  typography: lightThemeOptions.typography,
  shape: lightThemeOptions.shape,
  // Table head/cell overrides are now theme-aware (callback form in the light
  // options), so they adapt to dark mode automatically — no duplicates needed.
  components: lightThemeOptions.components,
};

// responsiveFontSizes scales the large heading variants down on small
// viewports so page titles (rendered as h4 by the shared headers) don't
// overflow or crowd action buttons on phones.
export const lightTheme = responsiveFontSizes(createTheme(lightThemeOptions));
export const darkTheme = responsiveFontSizes(createTheme(darkThemeOptions));

export const getTheme = (mode: "light" | "dark") => {
  return mode === "light" ? lightTheme : darkTheme;
};
